import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import api from "../../services/api";
import Loader from "../../components/loader";
import Table from "../../components/Table";
import Paginator from "../../components/Paginator";
import { ViewTabs, ColorLegend, getStatusColor, fmt, TIRANA_CUTOFF, isTirana } from "./shared";

const HEADER = [
  { title: "Name", key: "projectName" },
  { title: "Status", key: "status" },
  { title: "Remaining", key: "totalRemaining" },
  { title: "Payment missing since", key: "firstDueAt" },
  { title: "Last comment", key: "" },
  { title: "Next event if action", key: "date" },
];

const STATUS_OPTIONS = ["Timetable sent", "Signed timetable", "Payment in progress", "Late payment", "Late payment in progress", "In recovery"];

export default ({}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: "", query: {}, page: 1, pageSize: 50, sort: { created_at: -1 } });
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const { businessUnit } = useSelector((state) => state.Auth);
  // Post-cutoff defaults to on for Tirana (its books start Jan 2026) but is available everywhere.
  const [postCutoff, setPostCutoff] = useState(isTirana(businessUnit));
  const [overdueOnly, setOverdueOnly] = useState(false);

  useEffect(() => {
    get();
  }, [filters, businessUnit._id, postCutoff, overdueOnly]);

  async function get() {
    try {
      setLoading(true);

      const buFilter = businessUnit._id !== "ALL" ? { business_unit_id: businessUnit._id } : {};
      const cutoffFilter = postCutoff ? { invoiceDateFrom: TIRANA_CUTOFF } : {};
      const overdueFilter = overdueOnly ? { overdueInvoicesOnly: true } : {};

      const { ok, data, total } = await api.post(`/cashout/search`, { ...filters, ...buFilter, ...cutoffFilter, ...overdueFilter });

      setLoading(false);
      if (!ok) return toast.error("Something went wrong!");
      setData(data);
      setTotal(total);
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
  }

  if (!data) return <Loader />;

  const statuses = [...new Set(data.map((e) => e.status))];
  const activeStatus = filters.query?.status || null;

  const setStatusFilter = (status) => {
    const newQuery = { ...filters.query };
    if (!status || activeStatus === status) delete newQuery.status;
    else newQuery.status = status;
    setFilters({ ...filters, query: newQuery, page: 1 });
  };

  const handleSort = (key) => {
    setFilters({ ...filters, sort: { [key]: !filters.sort[key] ? 1 : filters.sort[key] === 1 ? -1 : undefined } });
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header: what & how much on the left, navigation on the right */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">
          Cashout plans <span className="text-lg font-semibold text-gray-500">· {fmt(data.reduce((acc, item) => acc + (item.totalRemaining || 0), 0))} remaining</span>
        </h1>
        <ViewTabs />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 bg-white border border-gray-200 rounded-lg p-3">
        <div className="w-56">
          <label className="block text-xs font-medium mb-1 text-gray-500">Status</label>
          <select value={activeStatus || ""} onChange={(e) => setStatusFilter(e.target.value || null)} className="block w-full border border-gray-200 rounded-md text-sm px-3 py-2 bg-white text-gray-700">
            <option value="">All</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 pb-2.5 text-xs text-gray-700 cursor-pointer" title="Only count invoices dated Jan 1, 2026 or later — money Tirana is responsible for">
          <input type="checkbox" checked={postCutoff} onChange={(e) => setPostCutoff(e.target.checked)} className="rounded border-gray-300" />
          Post-cutoff only (≥ Jan 2026)
        </label>

        <label className="flex items-center gap-2 pb-2.5 text-xs text-gray-700 cursor-pointer" title="Only count invoices whose due date has passed">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} className="rounded border-gray-300" />
          Overdue only
        </label>

        {activeStatus && (
          <button className="text-xs text-gray-500 underline hover:text-gray-700 pb-2.5" onClick={() => setStatusFilter(null)}>
            Clear filters
          </button>
        )}
      </div>

      {/* Amount per status — click a chip to filter on it */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {statuses.map((e) => {
            const isActive = activeStatus === e;
            return (
              <button
                key={e || "none"}
                onClick={() => setStatusFilter(e)}
                className={`px-2 py-1 text-sm rounded-md text-left cursor-pointer ${getStatusColor(e)} ${isActive ? "ring-2 ring-gray-500" : "hover:opacity-80"}`}>
                <div className="capitalize">{e ? e : "Non défini"}</div>
                <div className="font-semibold">{fmt(data.filter((item) => item.status === e).reduce((acc, item) => acc + item.totalRemaining, 0))}</div>
              </button>
            );
          })}
        </div>
        <ColorLegend />
      </div>

      <Table
        header={HEADER}
        total={total}
        loading={loading}
        sort={filters.sort}
        onSort={handleSort}
        height="min-h-min"
        sticky={true}
        noResultRender={<div className="w-full bg-white text-center p-2 my-5 rounded shadow-sm">Aucune résultat n'a été trouvé</div>}>
        {loading
          ? [...Array(filters.pageSize)].map((_, index) => (
              <tr key={index} className={`text-center cursor-pointer ${index % 2 === 0 ? "bg-neutral-50" : "bg-white"}`}>
                {HEADER.map((_, index) => (
                  <td key={index} className="py-4 px-3 max-w-xs">
                    <div className="h-2 bg-gray-200 rounded w-full" />
                  </td>
                ))}
              </tr>
            ))
          : data.map((e) => {
              const lastCommentText = e.comments.length ? e.comments[e.comments.length - 1].text : "";
              const lastCommentDate = e.comments.length ? e.comments[e.comments.length - 1].created_at.split("T")[0] : "";
              return (
                <tr
                  key={e.name}
                  onClick={(ev) => (ev.metaKey || ev.ctrlKey ? window.open(`/cashout/${e._id}`, "_blank") : navigate(`/cashout/${e._id}`))}
                  title="Open cashout plan"
                  className="border-b border-gray-200 hover:bg-gray-100 cursor-pointer">
                  <td className="py-1 px-2">
                    <Link to={`/cashout/${e._id}`} onClick={(ev) => ev.stopPropagation()} className="text-sm font-semibold text-left text-blue-600 hover:underline">
                      {e.projectName}
                    </Link>
                  </td>
                  <td className="py-1 px-2">
                    {e.status ? <span className={`px-2 py-1 rounded-full text-sm whitespace-nowrap ${getStatusColor(e.status)}`}>{e.status}</span> : <span className="text-sm text-gray-400">—</span>}
                  </td>
                  <td className="py-1 px-2 text-sm font-semibold tabular-nums whitespace-nowrap">{fmt(e.totalRemaining)}</td>
                  <td className="py-1 px-2">{e.firstDueAt ? new Date(e.firstDueAt).toISOString().slice(0, 10) : ""}</td>
                  <td className="py-1 px-2">{lastCommentText ? `${lastCommentDate} - ${lastCommentText.substring(0, 50)}` : <span className="text-gray-400">—</span>}</td>
                  <td className="py-1 px-2">{e.date ? e.date.split("T")[0] : ""}</td>
                </tr>
              );
            })}
        {!loading && data.length > 0 && (
          <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
            <td colSpan={2} className="py-2 px-2 text-sm text-left">
              Total — {data.length} plans
            </td>
            <td className="py-2 px-2 text-sm whitespace-nowrap tabular-nums">{fmt(data.reduce((acc, e) => acc + (e.totalRemaining || 0), 0))}</td>
            <td colSpan={3} />
          </tr>
        )}
      </Table>
      <div className="mt-1">
        <Paginator page={filters.page} setPage={(page) => setFilters({ ...filters, page })} last={Math.ceil(total / filters.pageSize)} />
      </div>
    </div>
  );
};
